import { Link } from "@/components/Link";
import { SingleColumn } from "@/layouts/single-column";

import { CONTACT_EMAIL } from "../../utils";

export default function LegalPrivacy() {
  return (
    <SingleColumn.Root>
      <SingleColumn.Main>
        <div className="card bg-base-100 p-6">
          <h2 className="text-3xl font-bold mb-8">Privacy Policy</h2>

          <article className="flex flex-col gap-1 text-justify">
            <p>
              <strong>Privacy Policy</strong>
            </p>
            <p>
              <strong>Effective Date:</strong>
              May 14, 2024
            </p>
            <p>
              DreamVault is a platform that helps connect Builders with Dreamers they love. “DreamVault,” “we,” “us,”
              and “our” refer to DreamVault, Inc., this platform, and the services offered by DreamVault, including the
              DreamVault iOS app, the DreamVault Android app, DreamVault APIs,{" "}
              <a target="_blank" href="http://www.dreamvault.co">
                <span className="text-primary">www.dreamvault.co</span>
              </a>{" "}
              and related websites. This Privacy Policy is part of our{" "}
              <Link className="text-primary underline" to="/legal/terms">
                Terms of Use
              </Link>
              , and describes how we collect, use, and share information.
            </p>
            <p>
              <strong>INFORMATION YOU PROVIDE DREAMVAULT</strong>
            </p>
            <p>
              This is information that you provide to us through text fields, such as your name, payment information,
              and benefits. The information of this type that we require you to provide differs depending on whether you
              create an account, become a Builder, purchase a subscription or other offering, otherwise engage with a
              Dreamer, or become a Dreamer, and typically includes at least:
            </p>
            <ul>
              <li>first and last name/surname;</li>
              <li>email address;</li>
              <li>phone number;</li>
              <li>username;</li>
              <li>password; and/or</li>
              <li>county and state/province of residence.</li>
            </ul>
            <p>
              You may also have the option to sign up for DreamVault using a Facebook, Google, or Apple account. We will
              ask for permission to access basic information from your Facebook, Google, or Apple account, such as your
              userID, full name, email address, and profile picture. You can choose to stop sharing that information
              with us at any time by going to Facebook, Google, or Apple to remove DreamVault&apos;s access to that
              account and your ability to use it to log in to DreamVault. In order to use an account with one of these
              companies to sign up for DreamVault, these companies may require you to share personal data with them,
              including information about your usage of DreamVault. As applicable to you, please consult the{" "}
              <a target="_blank" href="https://www.facebook.com/privacy/policy">
                <span className="text-primary">
                  <strong>Facebook</strong>
                </span>
              </a>
              ,{" "}
              <a target="_blank" href="https://policies.google.com/privacy">
                <span className="text-primary">
                  <strong>Google</strong>
                </span>
              </a>
              , and{" "}
              <a target="_blank" href="https://www.apple.com/legal/privacy/">
                <span className="text-primary">
                  <strong>Apple</strong>
                </span>
              </a>{" "}
              privacy policies to learn more about that.
            </p>
            <p>
              You may also have the option to add more information to your public profile, such as a location, social
              media links, and an about section, which we store along with the other information that we collect.
            </p>
            <p>
              <strong>Builders</strong>
            </p>
            <p>
              A Builder is someone who follows Dreamers and makes a purchase on DreamVault, which may come with special
              benefits from Dreamers they are following. As a Builder, you must provide your payment information to our
              payment partners. You can see the Privacy Policy for these payment partners on the{" "}
              <a target="_blank" href="https://venmo.com/legal/us-privacy-policy/">
                <span className="text-primary">
                  <strong>Venmo</strong>
                </span>
              </a>
              ,{" "}
              <a target="_blank" href="https://www.apple.com/legal/privacy/en-ww/">
                <span className="text-primary">
                  <strong>Apple</strong>
                </span>
              </a>
              ,{" "}
              <a target="_blank" href="https://www.paypal.com/webapps/mpp/ua/privacy-full">
                <span className="text-primary">
                  <strong>PayPal</strong>
                </span>
              </a>
              , and{" "}
              <a target="_blank" href="https://stripe.com/us/privacy/">
                <span className="text-primary">
                  <strong>Stripe</strong>
                </span>
              </a>{" "}
              sites. DreamVault stores the expiry and postal code associated with your payment card, but does not store
              your full payment card number. Instead, DreamVault uses a service provider to create a token that
              represents your card&apos;s primary account number, which DreamVault then stores. If you choose to pay
              using a payment partner that requires you to provide to them your personal data like name and email
              address, such as PayPal, then that payment partner may provide that information to DreamVault.
            </p>
            <p>
              We collect and process information about the Dreamers you subscribe to and/or support, the memberships and
              other offerings you purchase, and what benefits you receive. As a Builder, if you select a membership or
              offering with a physical benefit then you will be asked to optionally provide your shipping information,
              including phone number. We may also derive your location from your self-disclosed country, your IP
              address, and/or your payment card.
            </p>
            <p>
              <strong>Dreamers</strong>
            </p>
            <p>
              A Dreamer is someone who creates a page on DreamVault to engage with Builders who purchase gifts and
              merchandise on DreamVault to support the Dreamer&apos;s creations. To become a Dreamer, you must create a
              page that describes what you are creating and any benefits you are offering. To receive payouts, you have
              to create an account with our payment partner{" "}
              <a target="_blank" href="https://stripe.com/privacy">
                <span className="text-primary">
                  <strong>Stripe</strong>
                </span>
              </a>
              . If you choose to receive payouts using a payment partner that requires you to provide to them your
              personal data like name and email address, such as PayPal, then that payment partner may provide that
              information to DreamVault. You may also be required to provide your bank account information to process
              payouts. You must also provide us with additional information for tax purposes. Depending on your location
              and the type of creations that you create, you may have to provide DreamVault some combination of the
              following:
            </p>
            <ul>
              <li>email address;</li>
              <li>phone number;</li>
              <li>username;</li>
              <li>password;</li>
              <li>first and last name/surname;</li>
              <li>business name;</li>
              <li>address;</li>
              <li>social security number and/or employer identification number;</li>
              <li>country of citizenship;</li>
              <li>tax identification number, company registration number, and/or VAT number;</li>
              <li>date of birth;</li>
              <li>place of birth;</li>
              <li>your nonprofit registration number, if you are a nonprofit business;</li>
              <li>bank account information;</li>
              <li>self identification as an Adult/18+ Dreamer; and/or</li>
              <li>copies of your ID card and proof of residency.</li>
            </ul>
            <p>
              <strong>ADDITIONAL INFORMATION WE COLLECT</strong>
            </p>
            <p>
              <strong>Automatically Collected Information</strong>
            </p>
            <p>
              We automatically receive information when you view creations on or otherwise interact with DreamVault,
              even if you have not created an account. For example, when you visit{" "}
              <a target="_blank" href="http://www.dreamvault.co">
                <span className="text-primary">www.dreamvault.co</span>
              </a>{" "}
              , use DreamVault&apos;s mobile apps, sign into DreamVault&apos;s services, or view or interact with an
              email from DreamVault, we may automatically receive information about you, including:
            </p>
            <ul>
              <li>your IP address;</li>
              <li>your approximate location (typically derived from your IP address);</li>
              <li>your browser and/or device type;</li>
              <li>your operating system;</li>
              <li>your language settings;</li>
              <li>the referring web page (including parameters contained therein);</li>
              <li>your mobile carrier;</li>
              <li>device information (including device and application IDs);</li>
              <li>your search terms (including those not submitted as queries);</li>
              <li>the links you click;</li>
              <li>the pages you visit/the creations you load or request;</li>
              <li>session length, dwell time, and duration of media experiences (including view time); and/or</li>
              <li>
                cookie information (further detailed in our{" "}
                <Link className="text-primary underline" to="/legal/policy/cookie">
                  Cookie Policy
                </Link>
                ).
              </li>
            </ul>
            <p>
              <strong>Messages and Comments on DreamVault</strong>
            </p>
            <p>
              When you send or receive messages or when you post or are mentioned in comments or messages on DreamVault,
              we collect the contents of those messages and comments as well as information about related activity,
              including the timestamp of those messages and comments and who has viewed those messages or comments.
            </p>
            <p>
              <strong>User Research Studies &amp; Surveys</strong>
            </p>
            <p>
              We may conduct research about current, former, and prospective Dreamers and Builders. From time to time,
              we may solicit your participation in such a research study and, if you choose to participate, then we may
              observe how you use DreamVault and may record video, audio, and images of you using DreamVault and your
              interview with DreamVault researchers. As part of these research studies we may also ask you to answer
              survey questions, including about how you use DreamVault and how you perceive DreamVault&apos;s services
              and brand. When you answer these questions, we will store your responses.
            </p>
            <p>
              <strong>Event Information &amp; Surveys</strong>
            </p>
            <p>
              From time to time, we may solicit your participation in surveys related to DreamVault events. In order to
              better understand and serve Dreamers and Builders, we may ask you for demographic information, including
              information related to your gender identity, ethnicity, race, age, sexual orientation, earnings, and
              accessibility, which you may optionally choose to provide. We collect such information to help us better
              understand the overall population of Dreamers and Builders that use DreamVault, to develop and market
              events for Dreamers and Builders, and to showcase Dreamers for greater discoverability, including at such
              events. When you complete these surveys, we store your survey responses, including any such demographic
              information you choose to provide.
            </p>
            <p>
              We may also request information from you at in-person and online events. This information may include your
              name, email address, mobile phone number, details about the types of creations you create, and survey or
              feedback responses.
            </p>
            <p>
              <strong>Information from Third-Party Accounts</strong>
            </p>
            <p>
              After creating a DreamVault account, you may be able to connect your social media account(s) (e.g.
              Twitter, Facebook, YouTube, TikTok, and Twitch) with DreamVault and we may collect and store some
              information related to those accounts, including:
            </p>
            <ul>
              <li>profile information, including userID, username, page name, and/or profile picture;</li>
              <li>follower and/or subscriber counts;</li>
              <li>relationship graph, including accounts you follow and that follow you;</li>
              <li>post and/or upload counts; and/or</li>
              <li>view, like, and/or comment counts.</li>
            </ul>
            <p>
              You may also grant DreamVault access to these third-party accounts in order for some DreamVault features
              to operate. When you connect such a third-party account to your DreamVault account, that third-party
              service will typically present a page that describes the information that DreamVault can access and/or
              permissions you are granting to DreamVault to take actions on your behalf. At any time, you may be able to
              revoke DreamVault&apos;s access to those accounts using the respective third party&apos;s settings page
              (e.g.{" "}
              <a target="_blank" href="https://security.google.com/settings/">
                <span className="text-primary">
                  <strong>here</strong>
                </span>
              </a>{" "}
              for Google).
            </p>
            <p>
              DreamVault may use YouTube API services. By using DreamVault in connection with YouTube API Services, you
              agree to be bound by the{" "}
              <a target="_blank" href="https://www.youtube.com/t/terms">
                <span className="text-primary">
                  <strong>YouTube Terms of Service</strong>
                </span>
              </a>
              .
            </p>
            <p>
              <strong>Promotions</strong>
            </p>
            <p>
              DreamVault may operate certain promotions from DreamVault-operated accounts on third party platforms such
              as Instagram, Twitter, and Facebook. Personal data collected by DreamVault as part of such promotions will
              be used in accordance with this Privacy Policy.
            </p>
            <p>
              <strong>INFORMATION COLLECTED BY THIRD PARTIES</strong>
            </p>
            <p>
              <strong>Cookies</strong>
            </p>
            <p>
              Third parties may use cookies on our website. A cookie is a small text file that a website stores on your
              computer or mobile device when you visit the site. Please see our{" "}
              <Link className="text-primary underline" to="/legal/policy/cookie">
                Cookie Policy
              </Link>{" "}
              for more information on the use of cookies on DreamVault.
            </p>
            <p>
              <strong>Social Media Features and Widgets</strong>
            </p>
            <p>
              We may use social media and related products and features, the Facebook Like button, and widgets, such as
              the Share button or similar interactive mini-programs that run on DreamVault. These features may collect
              your IP address and other personal data, including which page you are visiting on our site, and may set
              and/or read cookies. Social media features and widgets are either hosted by a third party or directly on
              DreamVault. For more information on the technologies used by these third parties, the information they
              collect, and how that information is used, please refer to their respective privacy policies. Below are
              links to the privacy policies of the most used such services for easy reference:
            </p>
            <ul>
              <li>
                Facebook, including Instagram:{" "}
                <a target="_blank" href="https://www.facebook.com/about/privacy/">
                  <span className="text-primary">
                    <strong>https://www.facebook.com/about/privacy/</strong>
                  </span>
                </a>{" "}
              </li>
              <li>
                Google, including YouTube:{" "}
                <a target="_blank" href="https://policies.google.com/privacy">
                  <span className="text-primary">
                    <strong>https://policies.google.com/privacy</strong>
                  </span>
                </a>{" "}
              </li>
              <li>
                Snapchat:{" "}
                <a target="_blank" href="https://snap.com/en-US/privacy/privacy-policy">
                  <span className="text-primary">
                    <strong>https://snap.com/en-US/privacy/privacy-policy</strong>
                  </span>
                </a>{" "}
              </li>
              <li>
                TikTok:{" "}
                <a target="_blank" href="https://www.tiktok.com/legal/privacy-policy-us">
                  <span className="text-primary">
                    <strong>https://www.tiktok.com/legal/privacy-policy-us</strong>
                  </span>
                </a>{" "}
              </li>
              <li>
                Twitch:{" "}
                <a target="_blank" href="https://www.twitch.tv/p/legal/privacy-notice/">
                  <span className="text-primary">
                    <strong>https://www.twitch.tv/p/legal/privacy-notice/</strong>
                  </span>
                </a>{" "}
              </li>
              <li>
                Twitter:{" "}
                <a target="_blank" href="https://twitter.com/privacy">
                  <span className="text-primary">
                    <strong>https://twitter.com/privacy</strong>
                  </span>
                </a>{" "}
              </li>
            </ul>
            <p>
              <strong>HOW WE USE INFORMATION</strong>
            </p>
            <p>As applicable, we use the information we collect to:</p>
            <ul>
              <li>
                comply with industry requirements, self-regulatory guidelines, US federal law, US state law, and
                international laws, including tax reporting requirements;
              </li>
              <li>
                verify your identity to comply with industry requirements, self-regulatory guidelines, US federal law,
                US state law, and international laws;
              </li>
              <li>enable you to sign in to your account;</li>
              <li>enable you to purchase offerings on DreamVault;</li>
              <li>process payments and payouts;</li>
              <li>apply VAT and/or sales tax;</li>
              <li>deliver benefits and other offerings;</li>
              <li>send physical merchandise to your shipping address;</li>
              <li>
                send you messages and notifications, including push notifications, email, and SMS/MMS as controlled by
                your notification preferences;
              </li>
              <li>provide you with information about DreamVault, DreamVault products, and offerings on DreamVault;</li>
              <li>provide you with customer support and reply to your questions;</li>
              <li>promote your DreamVault account and/or Dreamer page for greater discoverability;</li>
              <li>personalize the DreamVault experience, including recommending Dreamers to Builders;</li>
              <li>
                market DreamVault products and services to you or to audiences similar to you, including via
                personalized advertising (also referred to as online behavioral advertising or interest-based
                advertising) as controlled by your marketing preferences;
              </li>
              <li>organize and tailor in-person and online events;</li>
              <li>understand and analyze how you use the service;</li>
              <li>
                conduct research and development to improve DreamVault and develop our products and their features;
              </li>
              <li>
                prevent fraud, abuse, and illegal activities on DreamVault, including using automated processing and
                manual review; and/or
              </li>
              <li>provide you with reasonable accommodation if you notify us of a disability.</li>
            </ul>
            <p>
              <strong>INFORMATION SHARED WITH DREAMERS</strong>
            </p>
            <p>
              By purchasing offerings on DreamVault that are offered by a Dreamer, or otherwise engaging with or
              becoming a Builder of a Dreamer, you agree to share personal data with that Dreamer that may include:
            </p>
            <ul>
              <li>your full name in your DreamVault profile;</li>
              <li>your email address;</li>
              <li>your other DreamVault profile information;</li>
              <li>
                your physical address, city, state/province, and country if you select a membership or offering with a
                physical benefit and choose to provide your shipping information;
              </li>
              <li>
                your phone number, when you have signed up to support a Dreamer who engages with you via text messages,
                if you select a membership or offering with a physical benefit and choose to provide your shipping
                information, or when you have signed up for event notifications via text;
              </li>
              <li>
                all information about your pledge, including amount and start date but not your full payment card
                information; and
              </li>
              <li>
                some aggregated data about how you use DreamVault, including analytics like post views, video views, and
                video view duration.
              </li>
            </ul>
            <p>
              When you use DreamVault to send a message to another user, including a Dreamer, the contents of that
              message will be shared with the recipients you select.
            </p>
            <p>
              As part of offering and operating a Dreamer page and/or membership on DreamVault, a Dreamer may use one or
              more third-party services. These third parties may, for example, host creations or help deliver benefits,
              including shipping packages to Builders. Accordingly, Dreamers may share Builder personal data with such
              third-party services and/or enable Builders to elect to share personal data with such third-party services
              in order to receive offerings or membership benefits or otherwise participate in a membership or related
              event. You should review the privacy policies of these third-party services in order to understand how
              they collect, use, and share personal data.
            </p>
            <p>
              <strong>INFORMATION SHARED WITH COMMUNITIES ON DREAMVAULT</strong>
            </p>
            <p>
              By purchasing offerings on DreamVault that are offered by a Dreamer, otherwise engaging with or becoming a
              Builder of a Dreamer, or by creating a Dreamer page yourself, you are joining a DreamVault community
              associated with that Dreamer. This community is comprised of the Dreamer and people who have followed or
              purchased an offering on DreamVault that is offered by, or otherwise engaged with or become a Builder of
              that Dreamer, including those who follow that Dreamer for free. You agree to share personal data with that
              community that may include:
            </p>
            <ul>
              <li>your full name in your DreamVault profile for that community;</li>
              <li>your avatar image in your DreamVault profile for that community;</li>
              <li>your biographical description in your DreamVault profile for that community;</li>
              <li>your connected social media accounts in your DreamVault profile for that community;</li>
              <li>
                the offerings you&apos;ve purchased on DreamVault related to the Dreamer associated with that community;
              </li>
              <li>how long you&apos;ve supported the Dreamer associated with that community;</li>
              <li>
                the communities on DreamVault to which you and the person viewing your community profile both belong;
                and
              </li>
              <li>
                the chat rooms on DreamVault in which you and the person viewing your community profile both are or have
                been present.
              </li>
            </ul>
            <p>
              This personal data may be displayed in your community profile, which may have more information than your
              public profile and is visible to fellow community members. If you disable your community profile in your
              settings, then your public profile, which is described below, may be displayed to fellow community
              members.
            </p>
            <p>
              <strong>INFORMATION SHARED WITH THE PUBLIC</strong>
            </p>
            <p>The following information may be publicly accessible or otherwise shared with members of the public:</p>
            <ul>
              <li>
                Aggregated and otherwise non-personal data, including aggregated usage data in blog posts, press
                releases, or other ways to share information about DreamVault&apos;s usage and business.
              </li>
              <li>
                Comments, likes, and other reactions inherit the visibility of the associated post or message.
                Accordingly, comments, likes, and reactions on public posts or messages are public and comments, likes,
                and reactions on posts or messages with limited visibility are visible to the audience of users to whom
                the author has made the post or message available. If a non-public post or message is later made public,
                then the comments, likes, and reactions on that post or message may also become public.
              </li>
              <li>For Builders:</li>
              <ul>
                <li>
                  your public DreamVault profile, including your username, avatar image, and the date you created your
                  DreamVault account;
                </li>
                <li>social media accounts you&apos;ve connected to your DreamVault account;</li>
                <li>your public biographical description if you&apos;ve added one to your DreamVault account; and</li>
                <li>if your full public profile setting is enabled:</li>
                <ul>
                  <li>the Dreamers you support and the date you started supporting them;</li>
                  <li>the posts you&apos;ve liked; and</li>
                  <li>the comments you&apos;ve made.</li>
                </ul>
              </ul>
              <li>For Dreamers:</li>
              <ul>
                <li>
                  your public DreamVault profile, including your username, avatar image, the date you created your
                  DreamVault account, and location information you&apos;ve added;
                </li>
                <li>social media accounts you&apos;ve connected to your DreamVault account;</li>
                <li>your public biographical description if you&apos;ve added one to your DreamVault account;</li>
                <li>if your full public profile setting is enabled,</li>
                <ul>
                  <li>the Dreamers you support and the date you started supporting them;</li>
                  <li>the posts you&apos;ve liked; and</li>
                  <li>the comments you&apos;ve made;</li>
                </ul>
                <li>
                  the contents of your Dreamer page, including a description of your Dreamer page, the titles of all of
                  your posts, goals you&apos;ve set for your Dreamer page, whether your Dreamer page has a shop, and
                  information about any offerings you&apos;ve added to that shop, including the title, description, and
                  characteristics of those offerings;
                </li>
                <li>
                  if certain features such as followers or earnings is available and is enabled, then the amount of
                  followers and money earned on DreamVault from your Dreamer page;
                </li>
                <li>
                  the contents of your posts are made available to the audience you&apos;ve selected – for example,
                  public posts are public and posts limited to a selected membership tier are visible to anyone who
                  subscribes to that tier;
                </li>
                <li>
                  Builders of your Dreamer page may be notified when you become a Builder for another Dreamer page on
                  DreamVault if your full public profile setting is enabled; and
                </li>
                <li>
                  when you like or react to a comment on one of the posts on your Dreamer page this fact may be
                  displayed to the audience that can view the post based on the post&apos;s visibility setting.
                </li>
              </ul>
            </ul>
            <p>
              <strong>INFORMATION SHARED WITH THIRD PARTIES</strong>
            </p>
            <p>
              We never sell your information to third parties. Other than information that is shared with Dreamers,
              shared with communities on DreamVault, or shared with members of the public, we will only share data with
              third parties under the following circumstances:
            </p>
            <ul>
              <li>
                DreamVault&apos;s service providers: these are companies that are contractually engaged with us to
                provide us with services, including cloud hosting, content distribution, security, order fulfillment,
                email and document management, internal communication/chat, analytics, credit card processing,
                multi-currency settlement solutions, and fraud detection and prevention. We may share your personal data
                with such service providers subject to obligations consistent with this Privacy Policy and any other
                appropriate confidentiality and security measures, and on the condition that these third parties use
                your personal data only on DreamVault&apos;s behalf and pursuant to our instructions (service providers
                may use other non-personal data for their own benefit).
              </li>
              <li>Government and law enforcement agencies</li>
              <ul>
                <li>
                  Tax reporting: to satisfy obligations to report information to tax authorities, including reporting
                  information about Dreamers&apos; earnings on DreamVault, tax identification information, and
                  information related to transactional taxes such as Valued Added Tax, Goods and Services Tax, other
                  local service or sales taxes in non-US locations, and state sales tax in the United States.
                </li>
                <li>
                  Legal process and law enforcement: to protect the security or integrity of DreamVault, as well as to
                  protect the rights, property, and safety of DreamVault, its employees, Builders, Dreamers, or others,
                  or if we believe that disclosure is reasonably necessary to comply with a law, regulation, or other
                  valid legal process (e.g. subpoenas or warrants served on DreamVault). When legally permitted to do
                  so, and under appropriate circumstances, DreamVault will endeavor to notify you (typically by email)
                  when we intend to release your data pursuant to a valid government request, law-enforcement request,
                  or other legal process.
                </li>
              </ul>
              <li>
                Any third parties connected with the sale, merger, bankruptcy, sale of assets, or reorganization of our
                company: in such a circumstance the promises in this Privacy Policy apply to any data transferred to a
                new entity.
              </li>
              <li>
                Third-party services to which you&apos;ve connected your DreamVault account: when you connect your
                DreamVault account to other websites, apps, or services, you may be asked to give those websites, apps,
                or services access to information about your DreamVault account and/or permission to perform actions
                within your DreamVault account on your behalf. If you choose to take such an authorization action, then
                DreamVault will follow your instructions and grant the access or permission that you&apos;ve selected.
              </li>
            </ul>
            <p>
              <strong>YOUR PREFERENCES AND CONTROL OVER YOUR DATA</strong>
            </p>
            <p>
              <strong>Choosing Your Preferences</strong>
            </p>
            <p>
              On{" "}
              <a target="_blank" href="http://www.dreamvault.co">
                <span className="text-primary">www.dreamvault.co</span>
              </a>{" "}
              and related mobile apps you may find your settings after you log in to your DreamVault account. Settings
              let you see and adjust your account preferences, including privacy preferences.
            </p>
            <p>
              <strong>Opting out of Marketing</strong>
            </p>
            <p>
              You may opt out of marketing at any time in every marketing communication as applicable. You may also
              adjust your preferences in your settings.
            </p>
            <p>
              Opting out will stop marketing emails and SMS/MMS as applicable. Please allow up to 30 days for your
              opt-out request to be processed. If you have an account with DreamVault, you will continue to receive
              service-related emails and texts if you have opted into receiving texts. You will also continue to receive
              service-related shipments of benefits to the designated delivery address.
            </p>
            <p>
              We may work with third-party advertising partners to market our services. You can opt your current browser
              out of interest-based advertising from partners that adhere to the Digital Advertising Alliance&apos;s
              Principles for Online Behavioral Advertising at{" "}
              <a target="_blank" href="https://optout.aboutads.info/">
                <span className="text-primary">
                  <strong>https://optout.aboutads.info</strong>
                </span>
              </a>
              . On Android, depending on your version of Android, you can delete the advertising ID for your current
              device in your Android phone&apos;s settings or you can opt your current device out of interest-based
              advertising by enabling “Opt out of Ads Personalization” in your Android phone&apos;s settings (precise
              directions and setting name may be different on different Android versions). On iOS 13 and previous
              versions only, you can opt your current device out of interest-based advertising by enabling the “Limit Ad
              Tracking” setting in your iOS phone&apos;s settings (precise directions may be different on different iOS
              versions). On iOS 14 and later versions only, if you don&apos;t want to allow apps to access your current
              device&apos;s iOS Identifier for Advertising, you can disable the “Allow Apps to Request to Track” setting
              in your iOS settings (precise directions may be different on different iOS versions).
            </p>
            <p>
              <strong>Turning Off Email Notifications</strong>
            </p>
            <p>
              You can change your email notifications in your email settings or click unsubscribe where indicated. While
              this allows you to stop many notification emails, we will still send other service-related emails.
            </p>
            <p>
              <strong>Turning Off Mobile Push Notifications</strong>
            </p>
            <p>
              If you download the DreamVault iOS or Android app you may also receive notifications on your mobile
              device. These can be disabled in the App settings.
            </p>
            <p>
              <strong>Controlling Your Data</strong>
            </p>
            <p>We enable individuals to:</p>
            <ul>
              <li>access their personal data that DreamVault has collected;</li>
              <li>correct or rectify any such personal data that they believe is inaccurate;</li>
              <li>restrict or oppose the processing of their personal data;</li>
              <li>delete their personal data; and</li>
              <li>transport their personal data.</li>
            </ul>
            <p>You can do this on DreamVault in the following ways:</p>
            <ul>
              <li>
                access, review, modify, and update your data by logging in to your account at DreamVault and going to
                youraccount settings;
              </li>
              <li>
                access your personal data, port it, or delete it data by emailing us at{" "}
                <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                  <span className="text-primary">{CONTACT_EMAIL}</span>
                </a>{" "}
                ;
              </li>
              <li>
                delete a previously-disabled account by emailing DreamVault at{" "}
                <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                  <span className="text-primary">{CONTACT_EMAIL}</span>
                </a>{" "}
                ; and
              </li>
              <li>exercising preferences as described above.</li>
            </ul>
            <p>
              If you are unable to log in to your account, and are unable to recover your account with a password reset
              in order to lodge your privacy request, then you may reach out to{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              . We reserve the right to refuse access to or recovery of an account, at our sole discretion, in order to
              prevent an unauthorized takeover of your account or unauthorized access to your personal data.
            </p>
            <p>
              <strong>Verification of Requests</strong>
            </p>
            <p>
              Users typically maintain password-protected accounts with DreamVault. If you submit a request to exercise
              your privacy rights, you will be asked to verify the request by logging in to your DreamVault account, or
              to verify your identity in another manner.
            </p>
            <p>
              If we are unable to adequately verify your request at our sole discretion then, in order to protect
              against unauthorized access to personal data, we reserve the right to deny the request.
            </p>
            <p>
              <strong>Do Not Track Signals</strong>
            </p>
            <p>
              Your browser may be configured to transmit a “Do Not Track” signal to websites that you visit. There is no
              industry-standard approach to what this signal means. Accordingly, DreamVault does not monitor or
              otherwise respond to these “Do Not Track” signals.
            </p>
            <p>
              <strong>CALIFORNIA PRIVACY LAWS</strong>
            </p>
            <p>
              The California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA) allows California
              residents to request access to the specific pieces and categories of personal information that the
              business has collected about the consumer, the categories of sources for that information, the business or
              commercial purposes for collecting the information, and the categories of third parties with which the
              information was shared.
            </p>
            <p>
              DreamVault provides California residents and all users with the right to view, access, or transport, or
              delete their information by emailing us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              . We do not discriminate against users who exercise their privacy rights.
            </p>
            <p>
              Under California&apos;s “Shine the Light” law (Civil Code section 1798.83), California residents have
              certain rights regarding the disclosure of their information to third parties for their own direct
              marketing purposes. DreamVault&apos;s policy is not to share your information with third parties for the
              third parties&apos; direct marketing purposes if you have exercised your option to prevent that. If you
              wish to not have your personal information shared for those purposes, you may adjust your preferences or
              request that we delete your personal information by emailing us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              .
            </p>
            <p>
              <strong>EU PRIVACY LAWS AND DATA TRANSFERS</strong>
            </p>
            <p>
              By using DreamVault, you agree that your personal data may be transferred to and stored in the United
              States, and handled as described in this Policy. DreamVault acts as a data controller, as defined under
              applicable law.
            </p>
            <p>
              DreamVault processes personal data based on different legal grounds depending on the circumstances. This
              includes performance of a contract, legitimate interest, legal obligation, and consent where appropriate.
              Where we rely on consent, you may withdraw such consent at any time.
            </p>
            <p>
              <strong>Contractual Protections Covering Transfers</strong>
            </p>
            <p>
              Where appropriate, DreamVault transfers of personal data of individuals in the European Union and the
              United Kingdom outside of those locations are performed based on contractual obligations placed on the
              recipient of the personal data.
            </p>
            <p>
              <strong>EU Data Privacy Requests</strong>
            </p>
            <p>
              If you have privacy concerns or questions, you can contact us via email at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
              with “EU Data Privacy Request” in the subject title. You may also post a letter to us with your questions
              at DreamVault LLC, 11509 Beatrice St., Los Angeles, CA, 90066.
            </p>
            <p>
              <strong>Complaints</strong>
            </p>
            <p>
              We take your privacy queries seriously, and are committed to resolving any complaints that may arise about
              our collection or use of your data. If you believe your privacy rights have been infringed, we encourage
              you to contact us at{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              . You may also have the right to complain to the appropriate data protection supervisory authority.
            </p>
            <p>
              <strong>Establishing an Authorized Agent</strong>
            </p>
            <p>
              You may designate an authorized agent to make a request on your behalf. In order to designate an
              authorized agent to make a request on your behalf, you must provide a valid power of attorney, the
              requester&apos;s valid government ID, and the authorized agent&apos;s valid government ID. Please contact{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>{" "}
              with your request. In order to prevent an unauthorized takeover of your account or unauthorized access to
              your personal data, we may decline access to or recovery of your account at our sole discretion.
            </p>
            <p>
              <strong>Our Data Retention Period</strong>
            </p>
            <p>
              DreamVault stores your personal data until it is no longer necessary to provide our services, or until you
              ask us to delete it. We may continue to retain some information even after you delete your account if we
              are required to do so in order to comply with applicable laws.
            </p>
            <p>
              <strong>SECURITY</strong>
            </p>
            <p>
              The security of your personal information is important to us. We use reasonable and appropriate physical,
              technical, and administrative industry safeguards to protect information from unauthorized access, use,
              loss, misuse or unauthorized alteration.
            </p>
            <p>
              Our systems never access your full credit card number, and we encrypt the most sensitive information like
              tax information using transport layer security (TLS). We also offer advanced security measures, such
              astwo-factor authenticationfor signing in, andCAPTCHA verificationin cases when a bot is detected, when a
              user attempts to login from a suspicious IP address or after rapid succession login attempts. We only
              serve traffic over HTTPS, which encrypts all data between you and us.
            </p>
            <p>
              <strong>CHILDREN</strong>
            </p>
            <p>
              DreamVault is not directed to children, and you may not use DreamVault if you are under the age of 13. You
              must also be old enough to consent to the processing of your personal data in your country (in some
              countries we may allow your parent or legal guardian to do so on your behalf). You must be at least 18
              years old or have your parent&apos;s or legal guardian&apos;s permission to have a Dreamer page on
              DreamVault or to make purchases.
            </p>
            <p>
              <strong>CHANGES</strong>
            </p>
            <p>
              We may sometimes make changes to this Privacy Policy. If we make a change to this Policy that, in
              DreamVault&apos;s sole discretion, is material, then we will let you know before the changes come into
              effect. Continuing to use DreamVault after a change to this policy means you accept the new policy. If you
              have any questions, please email{" "}
              <a target="_blank" href={`mailto:${CONTACT_EMAIL}`}>
                <span className="text-primary">{CONTACT_EMAIL}</span>
              </a>
              .
            </p>
          </article>
        </div>
      </SingleColumn.Main>
    </SingleColumn.Root>
  );
}
